import { useState, useEffect } from 'react'
import { useCopybookStore, DEFAULT_COPYBOOK_FONT } from '../store/copybookStore'

interface FontOption {
  id: number | string
  name: string
  importUrl?: string
  fontFamily: string
  pdfFontUrl?: string
}

// Selected fonts suitable for copybook / handwriting practice
const COPYBOOK_FONTS: FontOption[] = [
  {
    id: 0,
    name: '教育部標準楷書',
    fontFamily: '"TW-MOE-Std-Kai", "BiauKai", "教育部楷書", "DFKai-SB", sans-serif',
  },
  {
    id: 556,
    name: '隨峰體plus',
    importUrl: 'https://fontsapi.zeoseven.com/556/main/result.css',
    fontFamily: DEFAULT_COPYBOOK_FONT,
    pdfFontUrl: '/fonts/ThePeakFontPlus-Regular.ttf',
  },
  {
    id: 'builtin-slidexiaxing',
    name: '演示夏行楷',
    fontFamily: '"演示夏行楷", "Slidexiaxing-Regular", "Slidexiaxing", cursive, sans-serif',
    pdfFontUrl: '/fonts/Slidexiaxing-Regular.ttf',
  },
  {
    id: 'builtin-yingzhui',
    name: '英椎行書',
    fontFamily: '"英椎行書", "YingZhuiXingShu", "acgyosyo", cursive, sans-serif',
  },
  {
    id: 'builtin-zhimangxing',
    name: '鍾齊志莽行書',
    importUrl: 'https://fonts.googleapis.com/css2?family=Zhi+Mang+Xing&display=swap',
    fontFamily: '"Zhi Mang Xing", "鍾齊志莽行書", "钟齐志莽行书", cursive, sans-serif',
  },
  {
    id: 'builtin-masafont',
    name: '正風毛筆字體',
    fontFamily: '"正風毛筆字體", "masafont-Regular", "MasaFont", "衡山毛筆フォント行書", cursive, sans-serif',
  },

  { id: 5, name: 'AA', importUrl: 'https://fontsapi.zeoseven.com/5main/result.css', fontFamily: 'AA' },
  { id: 104, name: '清松手写体 5', importUrl: 'https://fontsapi.zeoseven.com/104/main/result.css', fontFamily: '清松手写体 5' },
  { id: 105, name: 'Zen Kurenaido', importUrl: 'https://fontsapi.zeoseven.com/105/main/result.css', fontFamily: 'Zen Kurenaido' },
  { id: 106, name: '草書', importUrl: 'https://fontsapi.zeoseven.com/106/main/result.css', fontFamily: '草書' },
  { id: 107, name: '草書 (简入繁出)', importUrl: 'https://fontsapi.zeoseven.com/107/main/result.css', fontFamily: '草書 (简入繁出)' },
  { id: 119, name: '快去写作业 CJK', importUrl: 'https://fontsapi.zeoseven.com/119/main/result.css', fontFamily: '快去写作业 CJK' },

  { id: 131, name: 'Klee One', importUrl: 'https://fontsapi.zeoseven.com/131/main/result.css', fontFamily: 'Klee One' },

  { id: 134, name: '霞鹜漫黑', importUrl: 'https://fontsapi.zeoseven.com/134/main/result.css', fontFamily: '霞鹜漫黑' },


  { id: 142, name: 'Tanugo', importUrl: 'https://fontsapi.zeoseven.com/142/main/result.css', fontFamily: 'Tanugo' },
  { id: 293, name: '霞鹜文楷 Mono', importUrl: 'https://fontsapi.zeoseven.com/293/main/result.css', fontFamily: 'LXGW WenKai Mono' },

  { id: 775, name: '字嶼雲遊手書TC', importUrl: 'https://fontsapi.zeoseven.com/775/main/result.css', fontFamily: '字嶼雲遊手書TC' },
]

interface ZeoSevenFontsProps {
  onSelect: (font: FontOption) => void
  selectedId?: number | string
}

interface LocalFontItem {
  name: string
  family: string
}

export default function ZeoSevenFonts({ onSelect, selectedId }: ZeoSevenFontsProps) {
  const store = useCopybookStore()
  const [loadedFonts, setLoadedFonts] = useState<Set<number | string>>(new Set([556, 9999]))
  const [activeId, setActiveId] = useState<number | string | undefined>(selectedId)
  const [isAdminMode, setIsAdminMode] = useState(false)

  // Local system fonts states
  const [customName, setCustomName] = useState('')
  const [customFamily, setCustomFamily] = useState('')
  const [detectedFonts, setDetectedFonts] = useState<LocalFontItem[]>([])
  const [showDetectedDropdown, setShowDetectedDropdown] = useState(false)
  const [hasApiSupport, setHasApiSupport] = useState(false)

  useEffect(() => {
    setHasApiSupport('queryLocalFonts' in window)
  }, [])

  useEffect(() => {
    setActiveId(selectedId)
  }, [selectedId])

  const loadFont = (font: FontOption) => {
    setActiveId(font.id)

    // Check if it's local font (id is string starting with 'local-')
    if (typeof font.id === 'string' && font.id.startsWith('local-')) {
      onSelect(font)
      return
    }

    if (font.id === 0 || font.id === 9999 || (typeof font.id === 'string' && font.id.startsWith('builtin-')) || !font.importUrl) {
      setLoadedFonts((prev) => new Set(prev).add(font.id))
      onSelect(font)
      return
    }

    if (loadedFonts.has(font.id)) {
      onSelect(font)
      return
    }

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = font.importUrl!

    const linkPromise = new Promise<void>((resolve, reject) => {
      link.onload = () => resolve()
      link.onerror = () => reject()
    })

    const fetchPromise = fetch(font.importUrl!)
      .then(res => res.text())
      .then(cssText => {
        const match = cssText.match(/font-family\s*:\s*["']?([^"';]+)["']?/i)
        return match && match[1] ? match[1].trim() : null
      })
      .catch(() => null)

    document.head.appendChild(link)

    Promise.all([linkPromise, fetchPromise]).then(([_, detectedFamily]) => {
      const updatedFont = { ...font }
      if (detectedFamily) {
        updatedFont.fontFamily = detectedFamily
        const idx = COPYBOOK_FONTS.findIndex(f => f.id === font.id)
        if (idx !== -1) {
          COPYBOOK_FONTS[idx].fontFamily = detectedFamily
        }
      }
      setLoadedFonts((prev) => new Set(prev).add(font.id))
      onSelect(updatedFont)
    }).catch(() => {
      console.error(`Failed to load font: ${font.name}`)
    })
  }

  // Scan local fonts via browser API
  const handleScanLocalFonts = async () => {
    try {
      // @ts-ignore
      const status = await navigator.permissions.query({ name: 'local-fonts' })
      if (status.state === 'denied') {
        alert('請在瀏覽器設定中允許讀取本機字型權限。')
        return
      }
      // @ts-ignore
      const localFonts = await window.queryLocalFonts()
      const unique: LocalFontItem[] = []
      const seen = new Set<string>()

      for (const font of localFonts) {
        if (!seen.has(font.family)) {
          seen.add(font.family)
          unique.push({ name: font.fullName, family: font.family })
        }
      }

      // Sort alphabetically
      unique.sort((a, b) => a.name.localeCompare(b.name, 'zh-Hans-CN'))
      setDetectedFonts(unique)
      setShowDetectedDropdown(true)
    } catch (err) {
      console.error(err)
      alert('無法偵測本機字型，請重試或手動輸入。')
    }
  }

  const handleAddLocalFont = (name: string, family: string) => {
    if (!name || !family) return
    // Format family with quotes if needed
    const formattedFamily = family.includes(' ') && !family.startsWith('"')
      ? `"${family}"`
      : family
    store.addLocalFont(name, formattedFamily)
    setCustomName('')
    setCustomFamily('')
    setShowDetectedDropdown(false)
  }

  // Combine standard and custom local fonts
  const allFontsList = [
    ...COPYBOOK_FONTS,
    ...store.localFonts.map(f => ({
      id: f.id,
      name: `[本機] ${f.name}`,
      fontFamily: f.fontFamily
    }))
  ]

  // Filter fonts for display based on admin mode settings
  const filteredFonts = allFontsList.filter(f =>
    isAdminMode || !store.disabledFontIds.includes(f.id as any)
  )

  return (
    <div className="space-y-4">
      {/* Label and Admin toggle */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-2">
        <label className="text-sm font-semibold text-gray-700">字型清單</label>
        <button
          type="button"
          onClick={() => setIsAdminMode(!isAdminMode)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-all ${isAdminMode
            ? 'bg-amber-50 text-amber-700 border-amber-200 font-medium'
            : 'bg-white text-gray-500 hover:text-gray-700 border-gray-200 shadow-sm'
            }`}
        >
          {isAdminMode ? '退出管理模式' : '⚙️ 管理字型列表'}
        </button>
      </div>

      {/* Font Select Grid */}
      <div className="grid grid-cols-1 gap-1.5 max-h-56 overflow-y-auto pr-1">
        {filteredFonts.length === 0 ? (
          <div className="text-xs text-gray-400 text-center py-4">無可用字型，請在管理模式中啟用或新增。</div>
        ) : (
          filteredFonts.map((font) => {
            const isDisabled = store.disabledFontIds.includes(font.id as any)
            const isLocal = typeof font.id === 'string' && font.id.startsWith('local-')

            return (
              <div
                key={font.id}
                className="flex items-center gap-1.5 w-full group"
              >
                <button
                  type="button"
                  onClick={() => !isAdminMode && loadFont(font)}
                  disabled={isAdminMode}
                  className={`flex-1 flex items-center justify-between p-2 rounded-xl border text-sm transition-all ${activeId === font.id
                    ? 'border-blue-600 bg-blue-50/70 text-blue-700 font-medium shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
                    } ${isDisabled ? 'opacity-40 line-through' : ''}`}
                >
                  <span className="truncate">{font.name}</span>
                  <span className="flex items-center gap-1 text-[10px]">
                    {isLocal && <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">本機</span>}
                    {loadedFonts.has(font.id) && !isLocal && font.id !== 0 && (
                      <span className="text-green-600 font-semibold">✓ 已載入</span>
                    )}
                  </span>
                </button>

                {/* Admin options */}
                {isAdminMode && (
                  <div className="flex items-center gap-1">
                    {/* Toggle visibility for standard ZeoSeven/System fonts */}
                    {!isLocal && font.id !== 0 && (
                      <button
                        type="button"
                        onClick={() => store.toggleFontDisabled(font.id)}
                        className={`p-2 rounded-lg border transition-all active:scale-90 ${isDisabled
                          ? 'bg-rose-50 text-rose-500 border-rose-100 hover:bg-rose-100'
                          : 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100'
                          }`}
                        title={isDisabled ? "點擊啟用此字型" : "點擊隱藏此字型"}
                      >
                        {isDisabled ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    )}

                    {/* Delete button for local fonts */}
                    {isLocal && (
                      <button
                        type="button"
                        onClick={() => store.removeLocalFont(font.id as string)}
                        className="p-2 rounded-lg border bg-rose-50 text-rose-500 border-rose-100 hover:bg-rose-100 active:scale-90"
                        title="刪除本機字型"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* User Custom Local Font controls */}
      <div className="bg-gray-50/60 rounded-2xl p-3.5 border border-gray-200/60 space-y-3">
        <span className="text-xs font-semibold text-gray-600 block">新增用戶端系統字型</span>

        {/* Detection via queryLocalFonts API */}
        {hasApiSupport && (
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleScanLocalFonts}
              className="w-full text-xs py-2 px-3 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl font-medium text-gray-700 flex items-center justify-center gap-1.5 shadow-sm active:scale-[0.98] transition-all"
            >
              <svg className="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              自動偵測本機已安裝字型
            </button>

            {showDetectedDropdown && detectedFonts.length > 0 && (
              <div className="space-y-1.5 bg-white p-2.5 rounded-xl border border-gray-200 shadow-sm max-h-36 overflow-y-auto">
                <label className="text-[10px] text-gray-400 block font-medium">選取偵測到的字型</label>
                <div className="space-y-1">
                  {detectedFonts.map((f, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleAddLocalFont(f.name, f.family)}
                      className="w-full text-left text-xs p-1.5 rounded hover:bg-blue-50 hover:text-blue-600 transition-colors truncate"
                    >
                      {f.name} ({f.family})
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manual Input Fields */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="字型顯示名稱 (例: 標楷體)"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              className="text-xs p-2 border border-gray-200 rounded-xl bg-white focus:border-blue-500 focus:outline-none w-full"
            />
            <input
              type="text"
              placeholder="CSS font-family (例: BiauKai)"
              value={customFamily}
              onChange={(e) => setCustomFamily(e.target.value)}
              className="text-xs p-2 border border-gray-200 rounded-xl bg-white focus:border-blue-500 focus:outline-none w-full"
            />
          </div>
          <button
            type="button"
            onClick={() => handleAddLocalFont(customName, customFamily)}
            disabled={!customName || !customFamily}
            className="w-full py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium text-xs rounded-xl shadow transition-colors"
          >
            手動新增本機字型
          </button>
        </div>
      </div>
    </div>
  )
}

export { COPYBOOK_FONTS }
export type { FontOption }
